/**
 * Página de Admin
 * Página protegida apenas para administradores
 * Gerencia usuários, médicos, pacientes e aprovações
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import {
  getDefaultRouteByRole,
  isAdminRole,
  shouldFetchAdminResources,
} from '@/lib/roleRouting';
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users,
  Stethoscope,
  FileText,
  Shield,
  Edit,
  Check,
  Flag,
  UserX,
  UserCheck,
  Search,
  X,
} from 'lucide-react';
import {
  listDoctors,
  listPatients,
  listAdmins,
  approveDoctor,
  getAdminStats,
  suspendUser,
  reactivateUser,
} from '@/services/index';
import { toast } from 'sonner';
import { GlobalSearch, NotificationCenter } from '@/components/dashboard/HeaderActions';
import { DoctorDetailsModal, PatientSummaryModal, UserStatusModal } from '@/components/admin';
import { DocumentViewerModal } from '@/components/documents/DocumentViewerModal';
import { getAdminDocumentViewUrl } from '@/services/doctors';
import { isDoctorActive, isPatientActive } from '@/utils/userStatus';
import type {
  DoctorDTO,
  PatientDTO,
  AdminDTO,
  AdminStats,
  DoctorDocumentDTO,
  DocumentDTO,
} from '@/services/index';

type TabType = 'doctors' | 'patients' | 'admins';
type StatusFilter = 'all' | 'active' | 'suspended';

// ── Search + Filter toolbar ───────────────────────────────────────────────
function SearchFilterBar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  placeholder,
  id,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
  placeholder: string;
  id: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          id={id}
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 text-sm bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg outline-none focus:ring-2 focus:ring-teal-500 dark:text-white transition"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex gap-1 bg-gray-100 dark:bg-neutral-800 rounded-lg p-1 shrink-0">
        {(['all', 'active', 'suspended'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => onStatusFilterChange(s)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              statusFilter === s
                ? 'bg-white dark:bg-neutral-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {s === 'all' ? 'Todos' : s === 'active' ? 'Ativos' : 'Inativos'}
          </button>
        ))}
      </div>
    </div>
  );
}



function patientDocumentToViewerDocument(doc: DocumentDTO): DoctorDocumentDTO {
  return {
    id: doc.id,
    documentType: doc.documentType,
    fileName: doc.fileName || doc.documentType,
    originalFileName: doc.fileName || doc.documentType,
    fileUrl: doc.fileUrl ?? undefined,
    mimeType: doc.fileName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream',
    fileSize: 0,
    hasFile: Boolean(doc.fileUrl),
    status: 'AVAILABLE',
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
  };
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading, logout } = useAuth();

  const activeTab = (searchParams.get('tab') as TabType) || 'doctors';

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [doctors, setDoctors] = useState<DoctorDTO[]>([]);
  const [patients, setPatients] = useState<PatientDTO[]>([]);
  const [admins, setAdmins] = useState<AdminDTO[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Search & filter — doctors
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctorStatusFilter, setDoctorStatusFilter] = useState<StatusFilter>('all');

  // Search & filter — patients
  const [patientSearch, setPatientSearch] = useState('');
  const [patientStatusFilter, setPatientStatusFilter] = useState<StatusFilter>('all');

  const doctorSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const patientSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Doctor details modal
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDTO | null>(null);
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<DoctorDocumentDTO | null>(null);
  // Separate state to hold the doctorId when viewing a document —
  // avoids relying on selectedDoctor being non-null after the details modal closes.
  const [viewingDoctorId, setViewingDoctorId] = useState<string | null>(null);

  // Patient summary modal
  const [selectedPatient, setSelectedPatient] = useState<PatientDTO | null>(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);

  // User status modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalAction, setStatusModalAction] = useState<'suspend' | 'reactivate'>('suspend');
  const [statusModalUser, setStatusModalUser] = useState<{ userId: string; name: string } | null>(null);

  const handleTabChange = (tab: TabType) => {
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (!isLoading && user && !isAdminRole(user.role)) {
      navigate(getDefaultRouteByRole(user.role), { replace: true });
    }
  }, [user, isLoading, navigate]);

  const loadDoctors = useCallback(async (search?: string, status?: StatusFilter) => {
    const resolvedStatus = status ?? doctorStatusFilter;
    const userStatus = resolvedStatus === 'all' ? undefined : (resolvedStatus as 'active' | 'suspended');
    const resp = await listDoctors(undefined, search ?? doctorSearch, userStatus, true);
    setDoctors(Array.isArray(resp.data) ? resp.data : []);
  }, [doctorSearch, doctorStatusFilter]);

  const loadPatients = useCallback(async (search?: string, status?: StatusFilter) => {
    const resolvedStatus = status ?? patientStatusFilter;
    const userStatus = resolvedStatus === 'all' ? undefined : (resolvedStatus as 'active' | 'suspended');
    const resp = await listPatients(search ?? patientSearch, userStatus);
    setPatients(Array.isArray(resp.data) ? resp.data : []);
  }, [patientSearch, patientStatusFilter]);

  const loadData = useCallback(async () => {
    setIsLoadingData(true);
    setError(null);
    try {
      const [adminsResp, statsResp] = await Promise.all([
        listAdmins(),
        getAdminStats(),
      ]);
      await Promise.all([loadDoctors(), loadPatients()]);
      setAdmins(Array.isArray(adminsResp.data) ? adminsResp.data : []);
      setStats(statsResp.data || null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erro ao carregar dados do sistema. Verifique a conexão.');
    } finally {
      setIsLoadingData(false);
    }
  }, [loadDoctors, loadPatients]);

  useEffect(() => {
    if (!shouldFetchAdminResources(user?.role, isLoading)) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading]);

  const handleDoctorSearchChange = (value: string) => {
    setDoctorSearch(value);
    if (doctorSearchTimer.current) clearTimeout(doctorSearchTimer.current);
    doctorSearchTimer.current = setTimeout(() => {
      loadDoctors(value, doctorStatusFilter);
    }, 400);
  };

  const handleDoctorStatusFilterChange = (status: StatusFilter) => {
    setDoctorStatusFilter(status);
    loadDoctors(doctorSearch, status);
  };

  const handlePatientSearchChange = (value: string) => {
    setPatientSearch(value);
    if (patientSearchTimer.current) clearTimeout(patientSearchTimer.current);
    patientSearchTimer.current = setTimeout(() => {
      loadPatients(value, patientStatusFilter);
    }, 400);
  };

  const handlePatientStatusFilterChange = (status: StatusFilter) => {
    setPatientStatusFilter(status);
    loadPatients(patientSearch, status);
  };

  const handleApproveDoctor = async (doctorId: string) => {
    try {
      setIsLoadingData(true);
      const result = await approveDoctor(doctorId);
      if (result.success) {
        toast.success('Médico aprovado com sucesso!');
        await loadDoctors();
      } else {
        toast.error(result.error || 'Erro ao aprovar médico');
      }
    } catch (err) {
      console.error('Error approving doctor:', err);
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleOpenDoctorModal = (doctor: DoctorDTO) => {
    setSelectedDoctor(doctor);
    setIsDoctorModalOpen(true);
  };

  const handleOpenPatientModal = (patient: PatientDTO) => {
    setSelectedPatient(patient);
    setIsPatientModalOpen(true);
  };

  const handleOpenStatusModal = (userId: string, name: string, action: 'suspend' | 'reactivate') => {
    setStatusModalUser({ userId, name });
    setStatusModalAction(action);
    setStatusModalOpen(true);
  };

  const handleConfirmUserStatus = async (reason: string) => {
    if (!statusModalUser) return;
    try {
      const fn = statusModalAction === 'suspend' ? suspendUser : reactivateUser;
      const result = await fn(statusModalUser.userId, reason);
      if (result.success) {
        const newUserStatus = statusModalAction === 'suspend' ? 'suspended' : 'active';
        const targetUserId = statusModalUser.userId;

        setDoctors((prev) =>
          prev.map((d) =>
            d.userId === targetUserId
              ? { ...d, user: d.user ? { ...d.user, status: newUserStatus } : d.user }
              : d
          )
        );
        setPatients((prev) =>
          prev.map((p) =>
            p.userId === targetUserId
              ? { ...p, user: p.user ? { ...p.user, status: newUserStatus } : p.user }
              : p
          )
        );

        toast.success(
          statusModalAction === 'suspend'
            ? 'Usuário inativado com sucesso.'
            : 'Usuário reativado com sucesso.'
        );

        setStatusModalOpen(false);
        setStatusModalUser(null);

        Promise.all([loadDoctors(), loadPatients()]).catch((err) =>
          console.error('Refetch after status change failed:', err)
        );
      } else {
        toast.error((result as { error?: string }).error || 'Erro ao atualizar status do usuário');
      }
    } catch (err) {
      console.error('Error updating user status:', err);
      toast.error('Erro ao conectar com o servidor');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAdminRole(user.role)) {
    return null;
  }

  const pendingCount = stats?.counts.pendingApprovals || 0;
  const getDoctorUserId = (doctor: DoctorDTO) => doctor.userId;
  const getPatientUserId = (patient: PatientDTO) => patient.userId;

  // ── Badge components ──────────────────────────────────────────────────────
  const ActiveBadge = () => (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
      <CheckCircle className="w-3 h-3" />
      Ativo
    </span>
  );

  const InactiveBadge = () => (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
      <XCircle className="w-3 h-3" />
      Inativo
    </span>
  );

  const PendingBadge = () => (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 animate-pulse shadow-sm">
      <AlertCircle className="w-3 h-3" />
      Pendente
    </span>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar userRole={user.role} userName={user.name} onLogout={handleLogout} />

      <div className="flex-1 flex flex-col">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Painel Administrativo
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Gerencie usuários, médicos e aprovações do sistema
            </p>
          </div>
          <div className="flex items-center gap-4">
            <GlobalSearch />
            <NotificationCenter />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 p-8 bg-gray-50 dark:bg-neutral-950">
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {pendingCount > 0 && (
            <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex flex-col">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Ações Pendentes</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Existem {pendingCount} médico(s) aguardando sua análise e aprovação.
                </p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-neutral-800 overflow-x-auto">
            {([
              { key: 'doctors', icon: Stethoscope, label: `Médicos (${doctors.length})` },
              { key: 'patients', icon: Users, label: `Pacientes (${patients.length})` },
              { key: 'admins', icon: Shield, label: `Administradores (${admins.length})` },
            ] as { key: TabType; icon: React.ElementType; label: string }[]).map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeTab === key
                    ? 'text-teal-600 dark:text-teal-400 border-b-2 border-teal-600'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {isLoadingData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin mr-2" />
              <p className="text-gray-600 dark:text-gray-400">Sincronizando dados...</p>
            </div>
          ) : (
            <>
              {/* ── Doctors Tab ── */}
              {activeTab === 'doctors' && (
                <div className="space-y-4">
                  <SearchFilterBar
                    search={doctorSearch}
                    onSearchChange={handleDoctorSearchChange}
                    statusFilter={doctorStatusFilter}
                    onStatusFilterChange={handleDoctorStatusFilterChange}
                    placeholder="Buscar por nome, e-mail ou CRM..."
                    id="doctor-search"
                  />
                  {doctors.length === 0 ? (
                    <div className="bg-white dark:bg-neutral-900 rounded-lg p-12 text-center border border-gray-200 dark:border-neutral-800">
                      <Stethoscope className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        {doctorSearch || doctorStatusFilter !== 'all'
                          ? 'Nenhum médico encontrado para os filtros aplicados'
                          : 'Nenhum médico registrado'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-800">
                      <table className="w-full">
                        <thead className="border-b border-gray-200 dark:border-neutral-800">
                          <tr>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Médico</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Especialidade</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">CRM</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Documentos</th>
                            <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Aprovação</th>
                            <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                            <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
                          {(doctors || []).map((doctor) => {
                            const active = isDoctorActive(doctor);
                            return (
                              <tr
                                key={doctor.id}
                                className={`hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors ${!active ? 'opacity-70' : ''}`}
                              >
                                <td className="px-6 py-4">
                                  <p className="font-medium text-gray-900 dark:text-white">{doctor.user?.name || 'N/A'}</p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500">{doctor.user?.email || ''}</p>
                                </td>
                                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{doctor.specialty}</td>
                                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{doctor.crm}/{doctor.crmState}</td>
                                <td className="px-6 py-4">
                                  {doctor.documents && doctor.documents.length > 0 ? (
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                                      <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
                                        {doctor.documents.length} {doctor.documents.length === 1 ? 'Doc' : 'Docs'}
                                      </span>
                                    </div>
                                  ) : doctor.professionalDocumentsUrl ? (
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      <span className="text-xs font-medium text-green-700 dark:text-green-300">✓ Legado</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                      <span className="text-xs font-medium text-red-700 dark:text-red-300">Pendente</span>
                                    </div>
                                  )}
                                </td>
                                {/* Aprovação profissional */}
                                <td className="px-6 py-4 text-center">
                                  {doctor.isApproved ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                      <CheckCircle className="w-3 h-3" />
                                      Aprovado
                                    </span>
                                  ) : (
                                    <PendingBadge />
                                  )}
                                </td>
                                {/* Status ativo/inativo — usa user.status como campo oficial */}
                                <td className="px-6 py-4 text-center">
                                  {active ? <ActiveBadge /> : <InactiveBadge />}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex justify-end gap-1">
                                    {!doctor.isApproved && active && (
                                      <button
                                        onClick={() => handleApproveDoctor(doctor.id)}
                                        disabled={!doctor.professionalDocumentsUrl && (!doctor.documents || doctor.documents.length === 0)}
                                        title={doctor.professionalDocumentsUrl || (doctor.documents && doctor.documents.length > 0) ? 'Aprovar Médico' : 'Documentos profissionais obrigatórios para aprovação'}
                                        className={`p-1 px-2 flex items-center gap-1 rounded transition-colors text-xs font-bold ${
                                          (doctor.professionalDocumentsUrl || (doctor.documents && doctor.documents.length > 0))
                                            ? 'text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 cursor-pointer'
                                            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                                        }`}
                                      >
                                        <Check className="w-4 h-4" />
                                        APROVAR
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleOpenDoctorModal(doctor)}
                                      title="Ver detalhes e documentos"
                                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    {/* Inativar/Reativar — baseado em user.status (campo oficial) */}
                                    {active ? (
                                      <button
                                        onClick={() => handleOpenStatusModal(getDoctorUserId(doctor), doctor.user?.name || 'Médico', 'suspend')}
                                        title="Inativar médico"
                                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      >
                                        <UserX className="w-4 h-4" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleOpenStatusModal(getDoctorUserId(doctor), doctor.user?.name || 'Médico', 'reactivate')}
                                        title="Reativar médico"
                                        className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                      >
                                        <UserCheck className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Patients Tab ── */}
              {activeTab === 'patients' && (
                <div className="space-y-4">
                  <SearchFilterBar
                    search={patientSearch}
                    onSearchChange={handlePatientSearchChange}
                    statusFilter={patientStatusFilter}
                    onStatusFilterChange={handlePatientStatusFilterChange}
                    placeholder="Buscar por nome, e-mail ou CPF..."
                    id="patient-search"
                  />
                  {patients.length === 0 ? (
                    <div className="bg-white dark:bg-neutral-900 rounded-lg p-12 text-center border border-gray-200 dark:border-neutral-800">
                      <Users className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        {patientSearch || patientStatusFilter !== 'all'
                          ? 'Nenhum paciente encontrado para os filtros aplicados'
                          : 'Nenhum paciente registrado'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-800">
                      <table className="w-full">
                        <thead className="border-b border-gray-200 dark:border-neutral-800">
                          <tr>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Paciente</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Email</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">CPF</th>
                            <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                            <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
                          {(patients || []).map((patient) => {
                            const active = isPatientActive(patient);
                            return (
                              <tr
                                key={patient.id}
                                className={`hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors ${!active ? 'opacity-70' : ''}`}
                              >
                                <td className="px-6 py-4">
                                  <p className="font-medium text-gray-900 dark:text-white">{patient.user?.name || 'N/A'}</p>
                                </td>
                                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{patient.user?.email || 'N/A'}</td>
                                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{patient.cpf || '-'}</td>
                                {/* Status ativo/inativo — usa user.status como campo oficial */}
                                <td className="px-6 py-4 text-center">
                                  {active ? <ActiveBadge /> : <InactiveBadge />}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => handleOpenPatientModal(patient)}
                                      title="Ver prontuário do paciente"
                                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    {/* Inativar/Reativar */}
                                    {active ? (
                                      <button
                                        onClick={() => handleOpenStatusModal(getPatientUserId(patient), patient.user?.name || 'Paciente', 'suspend')}
                                        title="Inativar paciente"
                                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      >
                                        <UserX className="w-4 h-4" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleOpenStatusModal(getPatientUserId(patient), patient.user?.name || 'Paciente', 'reactivate')}
                                        title="Reativar paciente"
                                        className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                      >
                                        <UserCheck className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Admins Tab ── */}
              {activeTab === 'admins' && (
                <div className="space-y-4">
                  {admins.length === 0 ? (
                    <div className="bg-white dark:bg-neutral-900 rounded-lg p-12 text-center border border-gray-200 dark:border-neutral-800">
                      <Shield className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Nenhum administrador registrado</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-800">
                      <table className="w-full">
                        <thead className="border-b border-gray-200 dark:border-neutral-800">
                          <tr>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Administrador</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Departamento</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
                          {(admins || []).map((admin) => (
                            <tr key={admin.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors">
                              <td className="px-6 py-4">
                                <p className="font-medium text-gray-900 dark:text-white">{admin.user?.name || 'N/A'}</p>
                                <p className="text-xs text-gray-400">{admin.user?.email || ''}</p>
                              </td>
                              <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{admin.department || '-'}</td>
                              <td className="px-6 py-4">
                                {admin.isActive ? <ActiveBadge /> : <InactiveBadge />}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}


            </>
          )}
        </main>
      </div>

      {/* Doctor Details Modal */}
      <DoctorDetailsModal
        doctor={selectedDoctor}
        isOpen={isDoctorModalOpen}
        onClose={() => { setIsDoctorModalOpen(false); setSelectedDoctor(null); }}
        onApprove={async () => {
          await loadDoctors();
          setIsDoctorModalOpen(false);
          setSelectedDoctor(null);
        }}
        onRejected={async () => {
          await loadDoctors();
          setIsDoctorModalOpen(false);
          setSelectedDoctor(null);
        }}
        isApproving={isLoadingData}
        onViewDocument={(doc) => {
          // Capture the doctorId NOW before the modal closes and selectedDoctor becomes null
          const doctorId = selectedDoctor?.id || null;
          setViewingDoctorId(doctorId);
          setViewingDoc(doc);
          setViewerOpen(true);
        }}
      />

      {/* Document Viewer */}
      <DocumentViewerModal
        isOpen={viewerOpen}
        onClose={() => { setViewerOpen(false); setViewingDoc(null); setViewingDoctorId(null); }}
        document={viewingDoc ? { ...viewingDoc, hasFile: viewingDoc.hasFile ?? true } : null}
        userId={viewingDoctorId || selectedPatient?.id || ''}
        fetchViewUrl={(id, docId) => {
          if (selectedPatient && !viewingDoctorId) {
            return Promise.resolve({
              success: true,
              data: {
                signedUrl: (viewingDoc as { fileUrl?: string } | null)?.fileUrl || '',
                fileName: (viewingDoc as { originalFileName?: string; documentType?: string } | null)?.originalFileName || (viewingDoc as { documentType?: string } | null)?.documentType || 'document',
                mimeType: (viewingDoc as { mimeType?: string } | null)?.mimeType || 'application/pdf',
                expiresAt: new Date(Date.now() + 86400000).toISOString(),
              },
            });
          }
          return getAdminDocumentViewUrl(id, docId);
        }}
      />

      {/* Patient Summary Modal */}
      <PatientSummaryModal
        patient={selectedPatient}
        isOpen={isPatientModalOpen}
        onClose={() => { setIsPatientModalOpen(false); setSelectedPatient(null); }}
        onViewDocument={(doc) => { setViewingDoc(patientDocumentToViewerDocument(doc)); setViewerOpen(true); }}
      />

      {/* User Status Modal */}
      <UserStatusModal
        isOpen={statusModalOpen}
        onClose={() => { setStatusModalOpen(false); setStatusModalUser(null); }}
        onConfirm={handleConfirmUserStatus}
        userName={statusModalUser?.name || ''}
        action={statusModalAction}
      />
    </div>
  );
}
